import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { hwcgEocCompanyMatches } from 'components/IncidentConfig/CreateIncident/hwcgEocNotice';
import apiConfig from 'constants/apiConfig';
import { useHwcgContactLists } from 'hooks/hwcgContactLists/hwcgContactListHooks';
import { useHwcgFlag } from 'utils/featureFlags';

type ExclusiveContact = {
  userGuid?: string | null;
  memberCompany?: string | null;
  memberCompanyExclusive?: boolean | null;
};

/** Mirrors COMS `hwcgExclusiveBlockedReason`; the server stays authoritative. */
export function hwcgExclusiveBlockedReason(
  contact: ExclusiveContact,
  responsibleParty: string | null | undefined
): string | null {
  if (contact.memberCompanyExclusive !== true) return null;
  if (hwcgEocCompanyMatches(contact.memberCompany, responsibleParty ?? '')) {
    return null;
  }
  const company = contact.memberCompany?.trim() || 'Their member company';
  return `Member Company Exclusive: ${company} is not the responsible party`;
}

export function hwcgActivationBlockedReason(
  contacts: ExclusiveContact[],
  userGuid: string,
  responsibleParty: string | null | undefined
): string | null {
  for (const contact of contacts) {
    if (contact.userGuid !== userGuid) continue;
    const reason = hwcgExclusiveBlockedReason(contact, responsibleParty);
    if (reason) return reason;
  }
  return null;
}

/** Why a roster seat's activation request would be refused, or null. */
export function useHwcgActivationBlockedReason(
  incidentId: string,
  groupId: string,
  userGuid: string
): string | null {
  const hwcg = useHwcgFlag();
  const lists = useHwcgContactLists({ enabled: hwcg, groupGuid: groupId });
  const contacts = (lists.data ?? []).flatMap((list) => list.members);
  const anyExclusive = contacts.some(
    (contact) => contact.memberCompanyExclusive === true
  );
  const groupActions = useQuery({
    queryKey: ['hwcg-group-actions', incidentId],
    queryFn: async () => {
      const response = await axios.get(
        apiConfig.apiGateway.hwcgIncidentGroupActions(incidentId)
      );
      return response.data as { responsibleParty?: string };
    },
    enabled: hwcg && anyExclusive && Boolean(incidentId),
  });
  if (!hwcg || !anyExclusive || !groupActions.data) return null;
  return hwcgActivationBlockedReason(
    contacts,
    userGuid,
    groupActions.data.responsibleParty
  );
}
